import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Shield, User, ChevronRight, AlertCircle } from 'lucide-react';
import { admin, type User as UserType } from '../../lib/api';

export default function AdminUsers() {
  const [users, setUsers] = useState<UserType[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [createErr, setCreateErr] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await admin.users(search, page);
      setUsers(res.users);
      setTotal(res.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateErr('');
    setCreateLoading(true);
    try {
      await admin.createUser({ email: newEmail, password: newPassword, full_name: newName, is_admin: newIsAdmin });
      setShowCreate(false);
      setNewEmail(''); setNewPassword(''); setNewName(''); setNewIsAdmin(false);
      load();
    } catch (err: any) {
      setCreateErr(err.message || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" /> User Management
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{total} total users</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Create User
        </button>
      </div>

      {/* Create user form */}
      {showCreate && (
        <div className="card border-blue-200 bg-blue-50 space-y-4">
          <h2 className="font-semibold text-gray-900">Create New User</h2>
          {createErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{createErr}</div>}
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Full name</label>
                <input type="text" className="input-field" placeholder="Jane Smith" value={newName} onChange={e => setNewName(e.target.value)} />
              </div>
              <div>
                <label className="label">Email <span className="text-red-500">*</span></label>
                <input type="email" className="input-field" placeholder="user@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} required />
              </div>
              <div>
                <label className="label">Password <span className="text-red-500">*</span></label>
                <input type="password" className="input-field" placeholder="Min. 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="newIsAdmin" checked={newIsAdmin} onChange={e => setNewIsAdmin(e.target.checked)} className="w-4 h-4" />
                <label htmlFor="newIsAdmin" className="text-sm font-medium text-gray-700">Administrator</label>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            className="input-field pl-9"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-secondary">Search</button>
      </form>

      {/* Users table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No users found</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {users.map(u => (
              <Link
                key={u.id}
                to={`/admin/users/${u.id}`}
                className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${u.is_admin ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  {u.is_admin ? <Shield className="w-4 h-4 text-blue-600" /> : <User className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{u.full_name || u.email}</div>
                  {u.full_name && <div className="text-sm text-gray-500 truncate">{u.email}</div>}
                </div>
                <div className="flex items-center gap-3 text-sm flex-shrink-0">
                  {!u.is_active && <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-medium">Disabled</span>}
                  {u.is_admin && <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs font-medium">Admin</span>}
                  <span className="text-gray-400">{new Date(u.created_at * 1000).toLocaleDateString()}</span>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary px-3 py-1.5 text-sm">Prev</button>
          <span className="flex items-center text-sm text-gray-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-secondary px-3 py-1.5 text-sm">Next</button>
        </div>
      )}
    </div>
  );
}
